/**
 * assembly/input/attachments.ts —— 草稿附件栏**纯内核**（U-1d 语义迁移；102 `assembly/input/attachments.js` 逐条译 TS）。
 * 依据：设计件 §7.3(a) 硬规则「附件选择顺序在 pending 中保留（按选择顺序展示）」／升级方案 §2 批 U-1d 行
 *   （有序草稿附件栏，可删**可排序**——排序是显式动作，不靠时间序）。
 * 纪律：零 DOM／零定时器／零请求（可测性）；时间/身份一律入参注入；输出冻结、输入不改。
 * 常量为旧面 `useChatEngine.js:33-35` 的同值迁移（MAX_DRAFT_FILES=5／20MB 上限／5MB 大文件阈值），
 * 旧面退役后以本文件为单一事实源。
 */

/** 附件条目可持有的文件形（DOM File 结构性满足；单测可用形似对象） */
export interface AttachmentFileLike {
  name: string;
  size: number;
  type?: string;
}

/** 附件状态机（旧面 draftCards 语义迁移；`done` 对应旧面 success） */
export type AttachmentStatus = "pending" | "uploading" | "done" | "error";

/** 附件条目快照（冻结；`file` 原样携带——上传通道要用原对象） */
export interface AttachmentItem {
  readonly id: string;
  readonly file: AttachmentFileLike;
  readonly name: string;
  readonly size: number;
  readonly status: AttachmentStatus;
  readonly fileId: string | null;
  readonly error: string | null;
  readonly sessionId: string | null;
}

/** 提交回执 meta（形制＝旧面 `body.context.files` 分量：filename/content_type/size；补 file_id 供 id 基引用） */
export interface AttachmentMeta {
  readonly filename: string;
  readonly content_type: string;
  readonly size: number;
  readonly file_id: string;
}

/** 护栏拒绝回执（warning 文案由调用方呈现，本层不渲染） */
export interface AttachmentRejected {
  readonly name: string;
  readonly reason: "max" | "oversize";
}

/** 草稿附件数上限（旧面 `useChatEngine.js:33` 同值） */
export const MAX_DRAFT_FILES = 5;
/** 单文件大小上限（旧面 `useChatEngine.js:34` 同值：20MB） */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** 大文件通道阈值（旧面 `useChatEngine.js:35` 同值：5MB ⇒ `upload-large`，否则 `chat-upload`） */
export const LARGE_FILE_BYTES = 5 * 1024 * 1024;

/** 附件状态闭集 */
export const ATTACHMENT_STATES = Object.freeze(["pending", "uploading", "done", "error"] as const);

/** 通道判定（§11.1 三行：chat-upload＝聊天上下文通道／upload-large＝分片通道；`/files/upload` 为产物域，Composer 不用） */
export function pickChannel(size: number): "chat-upload" | "upload-large" {
  return Number(size) > LARGE_FILE_BYTES ? "upload-large" : "chat-upload";
}

function snap(item: AttachmentItem): AttachmentItem {
  return Object.freeze({
    id: item.id,
    file: item.file,
    name: item.name,
    size: item.size,
    status: item.status,
    fileId: item.fileId ?? null,
    error: item.error ?? null,
    sessionId: item.sessionId ?? null,
  });
}

/**
 * 加入附件（护栏：总数上限／单文件超限显式拒绝——旧面 `addDraftFiles` 语义，警告文案由调用方呈现）。
 * 顺序＝**既有在前、新选追加**（选择顺序保留，§7.3 硬规则）。
 * @param existing 现有条目（不被改写）
 * @param files 新选文件（File 或形似对象）
 * @param opts.mintId 身份注入（缺省＝进程内序号 minter）
 * @returns rejected＝[{name,reason:'max'|'oversize'}]
 */
export function addFiles(
  existing: readonly AttachmentItem[],
  files: ReadonlyArray<AttachmentFileLike>,
  opts: { mintId?: () => string } = {},
): { items: readonly AttachmentItem[]; rejected: readonly AttachmentRejected[] } {
  const list: AttachmentItem[] = (Array.isArray(existing) ? existing : []).slice();
  const incoming = Array.isArray(files) ? files : [];
  const mintId = typeof opts.mintId === "function" ? opts.mintId : seqMinter();
  const validCount = (): number =>
    list.filter((c) => c.status === "pending" || c.status === "uploading" || c.status === "done").length;
  const rejected: AttachmentRejected[] = [];
  for (const f of incoming) {
    if (!f || typeof f.name !== "string") continue;
    if (validCount() >= MAX_DRAFT_FILES) {
      rejected.push(Object.freeze({ name: f.name, reason: "max" as const }));
      continue;
    }
    if (Number(f.size) > MAX_FILE_BYTES) {
      rejected.push(Object.freeze({ name: f.name, reason: "oversize" as const }));
      continue;
    }
    list.push(snap({ id: mintId(), file: f, name: f.name, size: Number(f.size) || 0, status: "pending", fileId: null, error: null, sessionId: null }));
  }
  return { items: Object.freeze(list.map(snap)), rejected: Object.freeze(rejected) };
}

let _seq = 0;
function seqMinter(): () => string {
  return () => {
    _seq += 1;
    return `att-${_seq}`;
  };
}

/** 删除一条（调用方负责 abort 在途请求——组件层职责） */
export function removeItem(items: readonly AttachmentItem[], id: string): readonly AttachmentItem[] {
  return Object.freeze((Array.isArray(items) ? items : []).filter((c) => c.id !== id).map(snap));
}

/**
 * 显式排序（升级方案 U-1d「可排序＝显式动作」）：delta=-1 上移／+1 下移；越界 ⇒ 原样返回（不抛）。
 */
export function moveItem(items: readonly AttachmentItem[], id: string, delta: number): readonly AttachmentItem[] {
  const list: AttachmentItem[] = (Array.isArray(items) ? items : []).slice();
  const from = list.findIndex((c) => c.id === id);
  if (from < 0) return Object.freeze(list.map(snap));
  const to = from + (Number(delta) || 0);
  if (to < 0 || to >= list.length) return Object.freeze(list.map(snap));
  const [row] = list.splice(from, 1);
  list.splice(to, 0, row);
  return Object.freeze(list.map(snap));
}

/** 待传条目（提交时进入上传通道） */
export function pendingItems(items: readonly AttachmentItem[]): readonly AttachmentItem[] {
  return (Array.isArray(items) ? items : []).filter((c) => c.status === "pending");
}

/** 失败条目（存在 ⇒ 拒发＋可读原因；旧面 `sendMessage` 与 `canSend` 双判据不一致的教训 ⇒ 单一来源） */
export function errorItems(items: readonly AttachmentItem[]): readonly AttachmentItem[] {
  return (Array.isArray(items) ? items : []).filter((c) => c.status === "error");
}

/** 提交回执 meta（形制＝旧面 `body.context.files` 分量：filename/content_type/size；补 file_id 供 id 基引用） */
export function metaOf(item: AttachmentItem): AttachmentMeta {
  return Object.freeze({
    filename: item.name,
    content_type: (item.file && item.file.type) || "application/octet-stream",
    size: item.size,
    file_id: item.fileId || "",
  });
}

/** 已传成附件的 meta 序（保持选择顺序 ⇒ 按栏内顺序输出） */
export function doneMetas(items: readonly AttachmentItem[]): readonly AttachmentMeta[] {
  return Object.freeze((Array.isArray(items) ? items : []).filter((c) => c.status === "done").map(metaOf));
}

export default {
  MAX_DRAFT_FILES,
  MAX_FILE_BYTES,
  LARGE_FILE_BYTES,
  ATTACHMENT_STATES,
  pickChannel,
  addFiles,
  removeItem,
  moveItem,
  pendingItems,
  errorItems,
  metaOf,
  doneMetas,
};
